/**
 * Map raw RTK Query errors from qclans-api into a small enum the CoC screens
 * can switch on, so components stay free of magic status codes and error-string
 * literals. Same shape as qchessErrors / qduelsErrors / qriotsErrors.
 *
 * qclans-api 4xx/5xx errors carry `{ error: "<code>" }` (no `{ data }` wrapper).
 */

import type { QclansErrorBody } from '@/types/qclans';

export type QclansErrorKind =
  | 'invalid_token' // POST verify with a wrong/expired in-game API token
  | 'player_not_found'
  | 'clan_not_found'
  | 'war_log_private' // 403 — the clan's war log isn't public
  | 'not_in_cwl' // 404 — clan isn't in a CWL season right now
  | 'rate_limited' // 429 — AI coach per-user throttle hit
  | 'ai_not_configured' // 503 — the API has no Anthropic key; hide the coach
  | 'invalid'
  | 'not_found'
  | 'auth'
  | 'network'
  | 'server'
  | 'unknown';

function isQclansErrorBody(data: unknown): data is QclansErrorBody {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { error?: unknown }).error === 'string'
  );
}

export function classifyQclansError(err: unknown): QclansErrorKind {
  const e = err as { status?: number | string; data?: unknown };

  if (e?.status === 'FETCH_ERROR' || e?.status === 'TIMEOUT_ERROR') {
    return 'network';
  }

  // Prefer the server-provided `error` code when present.
  if (isQclansErrorBody(e?.data)) {
    const code = e.data.error;
    if (code === 'invalid_token') return 'invalid_token';
    if (code === 'player_not_found') return 'player_not_found';
    if (code === 'clan_not_found') return 'clan_not_found';
    if (code === 'war_log_private') return 'war_log_private';
    if (code === 'not_in_cwl') return 'not_in_cwl';
    if (code === 'rate_limited') return 'rate_limited';
    if (code === 'ai_not_configured') return 'ai_not_configured';
    if (code === 'not_found') return 'not_found';
    if (code === 'invalid_body' || code === 'invalid_tag') return 'invalid';
    if (code === 'missing_bearer_token' || code === 'invalid_token') return 'auth';
  }

  // Fall back to HTTP status for codes we didn't get a body for.
  if (e?.status === 401) return 'auth';
  if (e?.status === 403) return 'war_log_private';
  if (e?.status === 429) return 'rate_limited';
  if (e?.status === 503) return 'ai_not_configured';
  if (e?.status === 404) return 'not_found';
  if (typeof e?.status === 'number' && e.status >= 500) return 'server';
  return 'unknown';
}

/** i18n key (coc namespace) for a classified qclans error. */
export function qclansErrorKey(kind: QclansErrorKind): string {
  return `error.${kind}`;
}

/**
 * Pull the `Retry-After` header (seconds) from a 429 error, if present. RTK
 * Query exposes response meta on the error object's `meta.response.headers`.
 * Returns null when absent/unparseable.
 */
export function retryAfterSeconds(err: unknown): number | null {
  const meta = (err as { meta?: { response?: Response } })?.meta;
  const header = meta?.response?.headers?.get?.('Retry-After');
  if (!header) return null;
  const n = parseInt(header, 10);
  return Number.isFinite(n) ? n : null;
}
